<template>
  <!-- 站点页面根容器，动态绑定字体等样式（sitePageStyle 来自租户全局字体配置） -->
  <div class="site-page" :style="sitePageStyle">
    <!-- 仅当接口返回了模板数据时才渲染；TemplateRenderer 负责把 CMS 模板 JSON 渲染成实际组件，wrap-node=false 表示不额外包裹节点 -->
    <TemplateRenderer v-if="pageData?.template" :template="pageData.template" :wrap-node="false" />
  </div>
</template>

<script setup lang="ts">
// ==================================================================
// 块1｜导入依赖
// ==================================================================
// Vue 组合式 API：computed 计算属性、provide 依赖注入、shallowRef 浅层响应式、watchEffect 副作用监听
import { computed, provide, shallowRef, watchEffect } from 'vue';
// 租户 SEO 头信息组合函数（拉取并生成 title/meta/canonical 等 head 配置）
import { useTenantSeoHead } from '~/composable/useTenantSeoHead';
// 为请求头附加当前域名信息的工具（用于后端识别租户）
import { withDomainHeaders } from '~/composable/useDomain';
// 分页路径解析/构建工具：把 /foo/page/2 解析成规范路径 + 页码，或反向拼接
import { parsePagedPath, buildPagedPath } from '~/utils/pagedPath';
// 根据全局字体配置解析出对应字体预设
import { resolveGlobalFontPreset } from '~/utils/globalFontPresets';
// 读取编辑站配置的全局背景色（globalConfig.colors.bgColor）
import { resolveGlobalBgColor } from '~/utils/globalColors';
// 多语言路由控制工具：解析语言前缀、构建本地化路径、规范化语言码、持久化语言、设置 html lang、获取语言元信息
import { parseLocalePath, buildLocalizedPath, normalizeLocale, persistLocale, applyHtmlLang, getLocaleMeta } from '~/utils/localeController';
// 租户语言相关工具：规范化租户配置的语言选项、解析租户主语言
import { normalizeTenantLanguageOptions, resolveTenantMainLanguage } from '~/utils/tenantLanguage';
// CMS 字体 @font-face 注入组合函数（按字体 key 与语言动态加载字体）
import { useCmsFontFace } from '~/composable/useCmsFontFace';

// ==================================================================
// 块2｜类型定义（接口 DTO 集中区）
// ==================================================================

// 站点页面接口 /api/site-page 的返回结构类型
type SitePageResponse = {
  label: string; // 页面标题标签（用于拼接 SEO title）
  template: any; // CMS 页面模板 JSON，交给 TemplateRenderer 渲染
  site?: {
    storeName?: string; // 店铺名称（用于拼接 SEO title）
  };
  globalConfig?: {
    typography?: Record<string, string>; // 排版配置，例如 fontFamily
    colors?: Record<string, string>; // 主题色板，例如 bgColor（页面背景色）
    siteMeta?: {
      faviconUrl?: string; // 站点 favicon 地址
      faviconVersion?: string; // favicon 版本号（用于缓存刷新）
    };
  };
};

// 分类列表接口 /api/tenant-categories 的返回结构类型（兼容 rows 直出或包在 data 里两种情况）
type CategoryListResponse = {
  rows?: Array<Record<string, unknown>>;
  data?: {
    rows?: Array<Record<string, unknown>>;
  };
};

// SEO 关键词接口返回的单条数据类型
type SeoKeywordItem = {
  keywords?: unknown[]; // 关键词数组
  description?: string | null; // 描述
  title?: string | null; // 标题
};

// SEO 关键词接口的完整响应类型
type SeoKeywordResponse = {
  msg: string;
  code: number;
  data?: SeoKeywordItem | null;
};

// SEO 文本接口返回的单条数据类型
type SeoTextItem = {
  url?: string | null; // 对应的页面 URL
  title?: string | null; // 标题
  description?: string | null; // 描述
  seoText?: string | null; // SEO 文本（可作关键词兜底）
};

// SEO 文本接口的完整响应类型
type SeoTextResponse = {
  msg: string;
  code: number;
  data?: SeoTextItem[];
};

// ==================================================================
// 块3｜纯工具函数（无副作用 helper，主要服务于块7 的旧链接重定向）
// ==================================================================

// 从分类接口响应中安全取出行数组（兼容两种数据结构），非数组时返回空数组
const pickCategoryRows = (resp: CategoryListResponse | null | undefined): Array<Record<string, unknown>> => {
  const rows = resp?.rows ?? resp?.data?.rows;
  return Array.isArray(rows) ? rows : [];
};

// 按候选字段名列表依次读取分类行的文本值，返回第一个非空的字符串（去除首尾空格）
const pickCategoryText = (row: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = row[key]; // 取当前候选字段的值
    if (value != null && String(value).trim()) return String(value).trim(); // 命中非空值即返回
  }
  return ''; // 全部字段为空则返回空串
};

// 规范化分类路径：去掉 hash/query、补前导斜杠、合并多余斜杠、去掉末尾斜杠
const normalizeCategoryPath = (value: unknown): string => {
  const raw = String(value ?? '').trim(); // 转成字符串并去空格
  if (!raw) return ''; // 空值直接返回空串
  const withoutHash = raw.split('#')[0] || ''; // 去掉 # 之后的部分
  const withoutQuery = withoutHash.split('?')[0] || ''; // 去掉 ? 之后的查询串
  const prefixed = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`; // 确保以 / 开头
  return prefixed.replace(/\/+/g, '/').replace(/\/+$/g, '') || '/'; // 合并连续斜杠并去尾斜杠，空则回退为根路径
};

// ==================================================================
// 块4｜路由 & 语言解析（从当前 URL 解析出语言 / 路径 / 分页页码）
// ==================================================================

// 获取当前路由对象
const route = useRoute();
// 解析当前完整路径中的语言前缀，得到 { locale, canonicalPathname, ... }
const localeRoute = computed(() => parseLocalePath(route.fullPath || '/'));
// 路由中显式携带的语言码（规范化后）；不存在语言前缀时为 null
const routeLocale = computed(() => (localeRoute.value.locale ? normalizeLocale(localeRoute.value.locale) : null));
// 请求语言：优先路由语言，否则回退英文 en
const requestLocale = computed(() => normalizeLocale(routeLocale.value || 'en'));

// 去掉语言前缀后的页面规范路径（根路径统一为 '/'）
const pagePath = computed(() => {
  const canonical = localeRoute.value.canonicalPathname || '/';
  return canonical === '/' ? '/' : canonical;
});

// 对规范路径再做分页解析，得到 { canonicalPath, pageNum, redirectTo, ... }
const pagedPath = computed(() => parsePagedPath(pagePath.value));

// 从 query 中解析旧式分页参数 ?pageNum=，非法或小于 1 时回退为 1
const pageQueryNum = computed(() => {
  const raw = route.query.pageNum; // 原始 query 值
  const value = Array.isArray(raw) ? raw[0] : raw; // query 可能是数组，取第一个
  const parsed = Number.parseInt(String(value || '').trim(), 10); // 解析为整数
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1; // 合法正整数才采用，否则默认第 1 页
});
//debugger
console.log(localeRoute.value)

// ==================================================================
// 块5｜URL 拼装工具（读请求头 host/protocol，拼出各种完整绝对 URL，供 SEO 用）
// ==================================================================

// 服务端渲染时读取请求头（host/转发头/语言），客户端为空对象
const reqHeaders = import.meta.server ? useRequestHeaders(['host', 'x-forwarded-host', 'x-forwarded-proto', 'lang']) : {};
// 从转发头解析当前协议（http/https），默认 https；取逗号分隔的第一个值
const routeProtocol = computed(() =>
  String(reqHeaders['x-forwarded-proto'] || 'https')
    .split(',')[0]
    .trim(),
);
// 从转发头/host 解析当前主机名；取逗号分隔的第一个值
const routeHost = computed(() =>
  String(reqHeaders['x-forwarded-host'] || reqHeaders.host || '')
    .split(',')[0]
    .trim(),
);
// 由路径构建完整绝对 URL：客户端用 window.origin，服务端用协议 + host 拼接
const buildFullUrl = (path: string) => {
  if (import.meta.client) return new URL(path, window.location.origin).href; // 浏览器端直接基于当前源
  return routeHost.value ? `${routeProtocol.value}://${routeHost.value}${path}` : ''; // 服务端拼接，无 host 时返回空
};
// 规范化 canonical 路径：合并多余斜杠、去掉末尾斜杠，根路径统一为 '/'
const normalizeCanonicalPath = (path: string): string => {
  const compact = String(path || '/').replace(/\/{2,}/g, '/'); // 连续斜杠压缩为单个
  if (compact === '/') return '/'; // 根路径直接返回
  return compact.replace(/\/+$/, '') || '/'; // 去掉末尾斜杠，空则回退根路径
};
// 浏览器实际访问的完整 URL（含语言前缀与分页段）
const browserFullUrl = computed(() => buildFullUrl(route.fullPath || '/'));
// 规范路径（去分页）对应的完整 URL，作为 SEO 匹配的备选
const canonicalFullUrl = computed(() => buildFullUrl(pagedPath.value.canonicalPath));
// 当前页面 canonical 标签使用的完整 URL
const pageCanonicalUrl = computed(() => buildFullUrl(normalizeCanonicalPath(route.path || '/')));
// fullUrl 作为浏览器完整 URL 的别名，供 SEO 逻辑复用
const fullUrl = computed(() => browserFullUrl.value);
// 提前声明的页面数据浅引用，供 SEO head 读取 favicon 回退值（在 pageData 就绪后回填）
const pageDataForSeoHead = shallowRef<SitePageResponse | null>(null);

// ==================================================================
// 块6｜SEO Head 初始化 + 语言最终决策（拉租户 SEO 配置，定出本次生效语言）
// ==================================================================

// 初始化租户 SEO head：拉取配置并返回 seoHeadConfig 与 applyHead（注入到 useHead）
const { seoHeadConfig, applyHead } = await useTenantSeoHead({
  cacheKey: () => `seo-head-${fullUrl.value || 'empty'}-${canonicalFullUrl.value || 'empty'}`, // 缓存键：随 URL 变化
  candidateUrls: () => [fullUrl.value, canonicalFullUrl.value], // 用于后端匹配 SEO 配置的候选 URL
  locale: () => routeLocale.value || requestLocale.value, // 当前语言
  reqHeaders, // 透传请求头
  fallbackFaviconUrl: () => pageDataForSeoHead.value?.globalConfig?.siteMeta?.faviconUrl || '', // favicon 回退地址
  fallbackFaviconVersion: () => pageDataForSeoHead.value?.globalConfig?.siteMeta?.faviconVersion || '', // favicon 回退版本
});

// 租户主语言：由 SEO 配置的 mainLanguage/targetLanguage 决定，回退到请求语言
const mainLanguage = computed(() => resolveTenantMainLanguage(seoHeadConfig.value?.mainLanguage, seoHeadConfig.value?.targetLanguage, requestLocale.value));
// 最终生效语言：优先路由显式语言，否则用主语言
const resolvedLocale = computed(() => routeLocale.value || mainLanguage.value);
// 租户配置的可选语言列表（规范化后）
const tenantLanguageOptions = computed(() => normalizeTenantLanguageOptions(seoHeadConfig.value?.targetLanguage));
// 当前激活的语言选项（用于语言切换 UI 展示）
const activeLanguageOption = computed(() => {
  const activeCode = resolvedLocale.value; // 当前生效语言码
  return (
    // 优先从租户语言列表中找到匹配项
    tenantLanguageOptions.value.find((item) => item.code === activeCode) ||
    // 找不到时用内置语言元信息兜底构造一个选项
    (() => {
      const meta = getLocaleMeta(activeCode);
      return { code: meta.code, label: meta.label, emoji: meta.emoji };
    })()
  );
});

// ==================================================================
// 块7｜服务端重定向（仅 SSR）：语言前缀冗余 / 旧式 ?categoryId= / 旧式 ?pageNum= 的 301 迁移
// ==================================================================

// 以下重定向逻辑仅在服务端执行，保证 SEO 友好的 301 跳转
if (import.meta.server) {
  // 若路由语言前缀正好等于主语言，则去掉冗余前缀，301 跳到无前缀的规范路径
  if (routeLocale.value && routeLocale.value === mainLanguage.value) {
    await navigateTo(buildLocalizedPath(localeRoute.value.canonicalPath, routeLocale.value, mainLanguage.value), { redirectCode: 301 });
  }

  const legacyPageNum = pageQueryNum.value; // 旧式 ?pageNum= 分页值
  const rawCategoryId = Array.isArray(route.query.categoryId) ? route.query.categoryId[0] : route.query.categoryId; // 旧式 categoryId 参数（可能为数组）
  const legacyCategoryId = String(rawCategoryId || '').trim(); // 规范化后的旧式分类 ID
  const effectivePageNum = Math.max(pagedPath.value.pageNum, legacyPageNum); // 取路径分页与 query 分页中的较大值

  // 处理旧式 ?categoryId= 链接：查出对应分类的新路径后 301 跳转
  if (legacyCategoryId) {
    // 拉取分类列表用于按 ID 匹配
    const categoryResp = await $fetch<CategoryListResponse>('/api/tenant-categories', {
      query: {
        pageNum: 1,
        pageSize: 100,
        lang: routeLocale.value || requestLocale.value,
      },
      headers: {
        ...reqHeaders,
        lang: routeLocale.value || requestLocale.value,
      },
    }).catch(() => null); // 请求失败时吞掉错误返回 null

    // 在分类行中匹配 id/itemId/categoryId 等于旧式 ID 的那一条
    const matchedCategory = pickCategoryRows(categoryResp).find((row) => pickCategoryText(row, ['id', 'itemId', 'categoryId']) === legacyCategoryId);
    // 从匹配的分类中取出其新路径（urlHandler/path/href/link）并规范化
    const matchedCategoryPath = normalizeCategoryPath(matchedCategory ? pickCategoryText(matchedCategory, ['urlHandler', 'path', 'href', 'link']) : '');

    //debugger
    // 找到新路径则带上分页与语言前缀 301 跳转
    if (matchedCategoryPath) {
      await navigateTo(buildLocalizedPath(buildPagedPath(matchedCategoryPath, effectivePageNum), routeLocale.value || requestLocale.value, mainLanguage.value), { redirectCode: 301 });
    }
  }

  // 是否需要把旧式 ?pageNum= 分页重定向到 /page/N 形式
  const shouldRedirectLegacyPagination = legacyPageNum > 1 && route.query.pageNum !== undefined;
  if (shouldRedirectLegacyPagination) {
    // 旧式分页 -> 规范分页路径 301 跳转
    await navigateTo(buildLocalizedPath(buildPagedPath(pagedPath.value.canonicalPath, legacyPageNum), resolvedLocale.value, mainLanguage.value), { redirectCode: 301 });
  } else if (pagedPath.value.redirectTo) {
    // 分页解析给出的其它需重定向情况（如 /page/1 应回退到规范路径）
    await navigateTo(buildLocalizedPath(pagedPath.value.redirectTo, resolvedLocale.value, mainLanguage.value), { redirectCode: 301 });
  }
}

// ==================================================================
// 块8｜主数据请求 + 分发（拉 pageData → provide 给子组件 → 字体 / urlHandle）
// ==================================================================

// 拉取当前页面的 CMS 数据（模板、站点信息、全局配置）；随语言与分页路径变化重新请求
const { data: pageData, error } = await useAsyncData<SitePageResponse>(
  () => `site-page-${resolvedLocale.value}-${pagedPath.value.canonicalPath}-${pagedPath.value.pageNum}`, // 缓存键
  () =>
    $fetch('/api/site-page', {
      query: {
        path: pagedPath.value.canonicalPath, // 去分页的规范路径
        browserPath: route.fullPath || '/', // 浏览器实际路径（含分页/query）
        lang: resolvedLocale.value, // 当前语言
      },
      headers: {
        ...reqHeaders, // 透传原始请求头
        lang: resolvedLocale.value, // 语言头
        'x-soho-route-lang': resolvedLocale.value, // 自定义头：路由语言，供后端识别
        'x-soho-route-path': route.fullPath || '/', // 自定义头：路由完整路径
      },
    }),
  {
    server: true, // 允许服务端渲染时预取
    watch: [pagedPath, resolvedLocale], // 分页或语言变化时重新拉取
  },
);

// 页面数据就绪后回填给 SEO head（用于 favicon 等回退值）
watchEffect(() => {
  pageDataForSeoHead.value = pageData.value || null;
});

// 提供给 CMS 子组件的路由上下文（路径、完整 URL、当前页码等）
const cmsPageRouteContext = computed(() => ({
  browserPath: route.fullPath || '/', // 浏览器实际路径
  browserFullUrl: browserFullUrl.value, // 浏览器完整 URL
  canonicalPath: pagedPath.value.canonicalPath, // 规范路径
  canonicalFullUrl: canonicalFullUrl.value, // 规范完整 URL
  pageNum: Math.max(pagedPath.value.pageNum, pageQueryNum.value), // 生效页码（取两种分页较大者）
}));

// 提供给子组件的全局配置（来自接口 globalConfig）
const providedGlobalConfig = computed(() => pageData.value?.globalConfig || {});
// 提供给子组件的语言配置（主语言、当前语言、可选项、当前项）
const providedLocaleConfig = computed(() => ({
  mainLanguage: mainLanguage.value,
  activeLanguage: resolvedLocale.value,
  options: tenantLanguageOptions.value,
  activeOption: activeLanguageOption.value,
}));
// 通过 provide 向后代组件注入全局配置、路由上下文与语言配置
provide('cmsGlobalConfig', providedGlobalConfig);
provide('cmsPageRouteContext', cmsPageRouteContext);
provide('cmsLocaleConfig', providedLocaleConfig);
// 根据全局字体配置解析当前字体预设
const activeFontPreset = computed(() => resolveGlobalFontPreset(pageData.value?.globalConfig?.typography?.fontFamily));
// 按字体预设 key 与当前语言动态注入 @font-face，得到最终可用的字体族
const { resolvedFamily } = useCmsFontFace(
  () => activeFontPreset.value.key,
  () => resolvedLocale.value,
);
// 页面根容器的内联样式：注入 CSS 变量与字体族、背景色
const sitePageStyle = computed(() => {
  const bgColor = resolveGlobalBgColor(pageData.value?.globalConfig);
  return {
    '--cms-font-family': resolvedFamily.value,
    '--cms-bg-color': bgColor,
    fontFamily: resolvedFamily.value,
    background: bgColor,
  };
});
// 由规范路径生成 urlHandle（去掉前导斜杠），用于 SEO 关键词查询
const urlHandle = computed(() => (pagedPath.value.canonicalPath || '/').replace(/^\/+/, ''));

// 【调试】打印核心变量，便于排查路由/语言/分页/SEO URL 是否符合预期（变量变化时会重新打印）
watchEffect(() => {
  console.log('[slug page] 核心变量:', {
    env: import.meta.server ? 'server' : 'client', // 运行环境
    fullPath: route.fullPath, // 当前完整路径
    routeLocale: routeLocale.value, // 路由显式语言（无则 null）
    requestLocale: requestLocale.value, // 请求语言（含兜底）
    resolvedLocale: resolvedLocale.value, // 最终生效语言
    mainLanguage: mainLanguage.value, // 租户主语言
    canonicalPath: pagedPath.value.canonicalPath, // 去分页的规范路径
    pageNum: pagedPath.value.pageNum, // 路径分页页码
    pageQueryNum: pageQueryNum.value, // 旧式 ?pageNum= 分页
    urlHandle: urlHandle.value, // SEO 关键词查询用的 handle
    fullUrl: fullUrl.value, // 浏览器完整 URL
    canonicalFullUrl: canonicalFullUrl.value, // 规范完整 URL
    pageCanonicalUrl: pageCanonicalUrl.value, // canonical 标签 URL
    hasTemplate: !!pageData.value?.template, // 是否拿到模板
    pageLabel: pageData.value?.label, // 页面标签
  });
});

// ==================================================================
// 块9｜错误处理（主数据请求失败 → 抛错触发 Nuxt 错误页）
// ==================================================================

// 页面数据请求出错时抛出致命错误，触发 Nuxt 错误页
if (error.value) {
  throw createError({
    statusCode: (error.value as { statusCode?: number })?.statusCode || 500, // 透传状态码，默认 500
    statusMessage: (error.value as { statusMessage?: string })?.statusMessage || '页面加载失败', // 透传错误信息
    fatal: true, // 致命错误
  });
}

// ==================================================================
// 块10｜SEO 元信息：拉取 keywords / seoText 接口 → 按优先级计算 → 注入 <head>
// （相关 DTO 已上移至块2 集中区）
// ==================================================================

// 按 urlHandle 拉取该页面的 SEO 关键词/标题/描述
const { data: seoKeywordData } = await useAsyncData<SeoKeywordItem | null>(
  () => `seo-keywords-${urlHandle.value || 'home'}`, // 缓存键，首页用 home
  async () => {
    if (!urlHandle.value) return null; // 无 handle 直接返回空
    const resp = await $fetch<SeoKeywordResponse>('/api/biz/tenantSeoKeyword/keywordsByUrlHandle', {
      query: { urlHandle: urlHandle.value },
      headers: withDomainHeaders({ ...reqHeaders, lang: resolvedLocale.value }), // 带域名头与语言头
    });
    const data = resp?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null; // 数据非对象则视为无效
    return {
      // 关键词：过滤成非空字符串数组
      keywords: Array.isArray(data.keywords) ? data.keywords.map((s) => String(s).trim()).filter(Boolean) : [],
      description: String(data.description || '').trim() || null, // 描述去空格，空则 null
      title: String(data.title || '').trim() || null, // 标题去空格，空则 null
    };
  },
  { watch: [urlHandle], default: () => null }, // handle 变化时重取，默认 null
);

// 按完整 URL 列表拉取 SEO 文本（title/description/seoText）
const { data: seoTextData } = await useAsyncData<SeoTextItem | null>(
  () => `seo-text-${fullUrl.value || 'empty'}-${canonicalFullUrl.value || 'empty'}`, // 缓存键随 URL 变化
  async () => {
    const urls = Array.from(new Set([fullUrl.value, canonicalFullUrl.value].filter(Boolean))); // 去重后的候选 URL
    if (!urls.length) return null; // 无 URL 直接返回空
    const resp = await $fetch<SeoTextResponse>('/api/biz/tenant/seoText/byFullUrls', {
      method: 'POST',
      body: { urls }, // 批量传入候选 URL
      headers: withDomainHeaders({ ...reqHeaders, lang: resolvedLocale.value }),
    });
    if (!Array.isArray(resp?.data) || !resp.data.length) return null; // 无数据返回空
    // 优先匹配浏览器完整 URL，其次匹配规范 URL，都没有则取第一条
    return resp.data.find((item) => String(item?.url || '').trim() === fullUrl.value) || resp.data.find((item) => String(item?.url || '').trim() === canonicalFullUrl.value) || resp.data[0];
  },
  { watch: [fullUrl, canonicalFullUrl], default: () => null }, // URL 变化时重取，默认 null
);
// 页面默认标题：拼接 "页面标签 - 店铺名"，都为空则回退为 '站点'
const pageTitle = computed(() => {
  const pageLabel = pageData.value?.label?.trim() || ''; // 页面标签
  const siteName = pageData.value?.site?.storeName?.trim() || ''; // 店铺名
  if (pageLabel && siteName) return `${pageLabel} - ${siteName}`; // 两者都有则拼接
  return pageLabel || siteName || '站点'; // 否则取其一，都无则默认
});

// 客户端挂载时同步 html lang 属性并持久化当前语言
if (import.meta.client) {
  applyHtmlLang(resolvedLocale.value); // 设置 <html lang>
  persistLocale(resolvedLocale.value); // 记住语言选择
}
// 计算最终 SEO title，按优先级依次取值
const seoMetaTitle = computed(() => {
  // console.log('seoMetaTitle computed:', { seoKeywordData: seoKeywordData.value, seoTextData: seoTextData.value });
  // console.log('seoMetaTitle computed:', { seoHeadConfig: seoHeadConfig.value, pageTitle: pageTitle.value });
  // 这里seoTextData 和 seoHeadConfig里返回的 pageTitle 都是优先级最高的，优先于页面默认 label
  // 后端seoText/byFullUrl里做了 label - sitename 的拼接

  const seoTextTitle = String(seoTextData.value?.title || '').trim(); // 1) SEO 文本标题优先级最高
  if (seoTextTitle) return seoTextTitle;
  // byFullUrl 接口返回的租户全局 pageTitle，优先于页面默认 label
  const tenantPageTitle = String(seoHeadConfig.value?.pageTitle || '').trim(); // 2) 租户全局 pageTitle
  if (tenantPageTitle) return tenantPageTitle;

  const title = String(seoKeywordData.value?.title || '').trim(); // 3) 关键词接口标题
  return title || pageTitle.value; // 4) 最后回退到页面默认标题
});
// 计算 SEO description：优先关键词描述，其次 SEO 文本描述
const seoMetaDescription = computed(() => {
  const description = String(seoKeywordData.value?.description || '').trim(); // 关键词接口描述
  if (description) return description;
  const seoTextDescription = String(seoTextData.value?.description || '').trim(); // SEO 文本描述
  return seoTextDescription || undefined; // 都为空返回 undefined（不输出该 meta）
});
// 计算 og:url：优先 SEO 文本里的 url，其次当前完整 URL
const seoMetaUrl = computed(() => {
  const url = String(seoTextData.value?.url || '').trim();
  return url || fullUrl.value || undefined;
});
// 计算 keywords：优先关键词数组拼接，其次用 seoText 兜底
const seoMetaKeywords = computed(() => {
  const keywords = Array.isArray(seoKeywordData.value?.keywords) ? seoKeywordData.value?.keywords.map((item) => String(item).trim()).filter(Boolean) : []; // 规范化关键词数组
  if (keywords.length) return keywords.join(', '); // 有关键词则逗号拼接
  const seoText = String(seoTextData.value?.seoText || '').trim(); // 否则用 SEO 文本兜底
  return seoText || undefined; // 都为空返回 undefined
});

// 把上面计算出的 SEO 信息注入到页面 head（title/description/keywords/og:url/canonical/lang）
applyHead({
  lang: () => resolvedLocale.value, // html lang
  title: () => seoMetaTitle.value, // 页面标题
  description: () => seoMetaDescription.value, // 描述
  keywords: () => seoMetaKeywords.value, // 关键词
  ogUrl: () => seoMetaUrl.value, // og:url
  canonicalUrl: () => pageCanonicalUrl.value || undefined, // canonical 链接
});
</script>

<style scoped>
.site-page {
  min-height: 100vh;
  /* 背景色来自编辑站「全局配置 → 颜色 → 背景颜色」（内联注入 --cms-bg-color） */
  background: var(--cms-bg-color, #ffffff);
}
</style>
